import React, { useState } from 'react';
import { errorMessage } from '../../../api';
import { Button, ErrorState, Skeleton, TextField, Toggle } from '../../../ui';
import type { CityCacheEntry, GeoCity, GeoState, RegionSelection, SelectionChange } from '../../../types';
import { clearState, selectAllInState, setWholeState, stateStatus, toggleCity, citiesOfState } from '../regionsModel';

/**
 * Everything needed to choose regions inside ONE state: "entire state" switch, search,
 * select all / clear, and the city checklist. Used by both the map view and the list view.
 *
 * `onChange` receives an UPDATER function (sel) => newSel, like React's setState, so rapid clicks never
 * overwrite each other with a stale selection.
 * `entry` is the city cache entry for this state (see useCityCache) or undefined before loading starts.
 * This checklist is also the keyboard/screen-reader alternative to clicking dots on the map.
 */
interface CityPickerProps {
  state: GeoState;
  entry: CityCacheEntry | undefined;
  selection: RegionSelection;
  onChange: SelectionChange;
  onRetry: () => void;
  fill?: boolean;
}

export function CityPicker({ state, entry, selection, onChange, onRetry, fill = false }: CityPickerProps) {
  // `fill`: on wide windows (>= 1180 px) take the height of the parent and scroll the checklist inside it
  // (side-by-side map layout); on narrower ones, and when not `fill`, the checklist has its own max height.
  const [filter, setFilter] = useState('');
  const whole = stateStatus(selection, state) === 'whole';
  const items: GeoCity[] = (entry && entry.items) || [];
  const picked = citiesOfState(selection, state).length;
  const visible = filter ? items.filter((c) => c.name.toLowerCase().includes(filter.toLowerCase())) : items;

  return (
    <div className={fill ? 'flex min-h-0 flex-col min-[1180px]:h-full' : undefined}>
      <div className="mb-3 flex-none">
        <Toggle label={`Select entire ${state.name}`} checked={whole} onChange={(on) => onChange((sel) => setWholeState(sel, state, on))} />
      </div>

      {(!entry || entry.status === 'loading') && (
        <div className="space-y-2" aria-busy="true" aria-label="Loading cities">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      )}

      {entry && entry.status === 'error' && <ErrorState title="Could not load cities" message={errorMessage(entry.error)} onRetry={onRetry} />}

      {entry && entry.status === 'ok' && items.length === 0 && (
        <p className="rounded-lg bg-canvas px-3 py-6 text-center text-sm text-muted">No city data for {state.name}. You can still target the entire state with the switch above.</p>
      )}

      {entry && entry.status === 'ok' && items.length > 0 && (
        <div className={fill ? 'flex min-h-0 flex-col min-[1180px]:flex-1' : undefined}>
          <div className="mb-2 flex flex-none flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1"><TextField label="Search cities" type="search" placeholder={`Search ${items.length} cities…`} value={filter} onChange={(e) => setFilter(e.target.value)} /></div>
            {/* With a search active, "all" means the cities currently shown, not the hidden ones. */}
            <Button variant="secondary" size="sm" disabled={whole || visible.length === 0} onClick={() => onChange((sel) => selectAllInState(sel, state, visible))}>{filter ? `Select ${visible.length} shown` : 'Select all'}</Button>
            <Button variant="ghost" size="sm" disabled={whole || !picked} onClick={() => onChange((sel) => clearState(sel, state))}>Clear</Button>
          </div>
          {visible.length === 0 ? <p className="py-4 text-center text-sm text-muted">No cities match “{filter}”.</p> : (
            <ul aria-label={`Cities in ${state.name}`} className={`overflow-y-auto rounded-lg border border-line ${fill ? 'max-h-72 min-h-0 min-[1180px]:max-h-none min-[1180px]:flex-1' : 'max-h-72'}`}>
              {visible.map((city) => (
                <li key={city.id} className="border-b border-line/60 last:border-0 hover:bg-canvas">
                  <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-brand-600"
                      checked={whole || Boolean(selection.cities[city.id])}
                      disabled={whole}
                      onChange={() => onChange((sel) => toggleCity(sel, state, city))}
                    />
                    {city.name}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
